import React, { useCallback, useContext } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { isAxiosError } from 'axios';
import { Box, Divider, IconButton, Tooltip, Typography, Button, Stack, Link } from '@mui/material';
import { Google } from '@mui/icons-material';

import config from '@shared/config';
import { FeedbackContext, LocalizationContext } from '@cvt/contexts';
import { useRouter } from '@cvt/routing';

import { DirigoLogo } from '@shared/components/Logo/DirigoLogo';

import { useSignup } from '../hooks/useSignup';
import { UserSignupForm } from '../partials/UserSignupForm';
import { SignUpLayout } from '../components/Layout/SignUpLayout';
import { SignInBox } from '../components/Container/SignInBox';


const EMAIL_ALREADY_EXISTS_RESPONSE = 'User with this email already exists in database';

export const SignUp = () => {
  const router = useRouter();
  const { genericErrorFeedback, triggerFeedback } = useContext(FeedbackContext);
  const { dictionary } = useContext(LocalizationContext);

  const { signupAndLogin, signupWithSSO } = useSignup();

  const onSSOLogin = useCallback(async (provider: string) => {
    try {
      await signupWithSSO(provider);
    } catch (e: any) {
      triggerFeedback({
        severity: 'error',
        message: e.message,
      });
    }
  }, [signupWithSSO, triggerFeedback]);

  const onSubmit = useCallback(async (data: Auth.SignupWithEmailAndPassword) => {
    try {
      await signupAndLogin(data);
      router.welcome.go();
    } catch(e) {
      console.error(e);

      if (isAxiosError<string>(e) && e.response?.data === EMAIL_ALREADY_EXISTS_RESPONSE) {
        triggerFeedback({
          severity: 'error',
          message: 'Email already exists',
        });
      } else {
        genericErrorFeedback();
      }
    }
  }, [genericErrorFeedback, triggerFeedback, signupAndLogin, router]);

  return (
    <SignUpLayout>
      <Box height="100%" display="flex" flexDirection="column" justifyContent="center">
        <Stack spacing={2} alignItems="center" mb={4} textAlign="center">
          <DirigoLogo/>
          <Typography variant="h1" color="buttonWhite" fontWeight={700}>{dictionary.auth.signUp.title}</Typography>
          <Typography variant="h3" color="textSecondary">Start your leadership storytelling journey</Typography>
        </Stack>

        <SignInBox>
          <Stack direction="row" justifyContent="center" spacing={2} >
            {config.featureFlags.googleSSO && (
              <Button
                color="buttonWhite"
                variant="contained"
                size="large"
                fullWidth
                startIcon={<Google/>}
                onClick={() => onSSOLogin('google')}
              >
            Continue with Google
              </Button>
            )}
            {config.featureFlags.microsoftSSO && (
              <Tooltip title={dictionary.auth.login.buttonLoginWithMicrosoft}>
                <IconButton onClick={() => onSSOLogin('microsoft')}>
                  <img src="/microsoft.svg" alt="Microsoft" width={32} height={32}/>
                </IconButton>
              </Tooltip>
            )}
          </Stack>
          <Box my={2}>
            <Divider orientation="horizontal" flexItem>
              {dictionary.auth.divider}
            </Divider>
          </Box>

          <Box>
            <UserSignupForm
              hiddenFields={['phone']}
              onSubmitRequest={onSubmit}
              onSubmitButtonText={dictionary.auth.signUp.buttonSignup}
            />
          </Box>
        </SignInBox>
        <Box mt={4} textAlign="center">
          <Typography variant="body2" color="textPrimary">
            {dictionary.auth.signUp.alreadyHaveAccount}{' '}
            <Link
              component={RouterLink} to={router.auth.login.path}
              color="primaryAlt"
              underline="hover"
            >
              {dictionary.auth.signUp.buttonLogin}
            </Link>
          </Typography>
        </Box>
      </Box>
    </SignUpLayout>
  );
};
